import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../assets/styles/faq.css";
import Logo from "../assets/img/icons/faqlogo.png";
import JoinCommunityAndFooter from "../components/landing/JoinCommunityAndFooter";

// Types
type QAItem = { question: string; answer: string };
type SubTopic = { id: string; title: string; qas: QAItem[] };
type Topic = { id: string; title: string; subTopics: SubTopic[] };

const faqData: Topic[] = [
	{
		id: "about-dfiance",
		title: "About Dfiance",
		subTopics: [
			{
				id: "business-model",
				title: "Business Model",
				qas: [
					{
						question: "Is Dfiance free to play?",
						answer:
							"Yes! Dfiance is completely free to play; While a season pass exists, it is not required to play the game and does not make the player stronger. Skill, strategy, and a good amount of luck are the key factors that decide the course of the battle.",
					},
					{
						question: "How will the developers make money from the game?",
						answer:
							"Our desired model is oriented at cosmetics (skins, card backs, SFX/VFX, avatars, etc), Quality of Life improvements (e.g. spare deck slots) and special events access. Our goal is to keep pay to win elements at or close to zero. The battle pass is cosmetic / QoL oriented with minimum or no gameplay benefits.",
					},
					{
						question: "Is it possible to buy cards with real money?",
						answer: "No, it is not possible to buy cards with real money. For the collectors among us, it will be to buy physical copies of the cards in the future.",
					},
					{
						question: "Is there a battle pass?",
						answer: "Yes, it is called the Season Pass. However it will be mainly cosmetic / QoL oriented with minimum or no gameplay benefits.",
					},
				],
			},
			{
				id: "game-availability",
				title: "Platforms",
				qas: [
					{
						question: "What platforms is Dfiance available on?",
						answer: "Dfiance will launch on Browser, Steam and mobile platforms.",
					},
					{
						question: "Will you be launching a mobile version?",
						answer: "Yes, Dfiance will be available on mobile platforms later this year. We will initially launch with an Android client, with an iOS client to follow.",
					},
					{
						question: "Is Dfiance cross-platform ? (If i play on Browser, will I be matched against players that are playing from a Steam client?)",
						answer: "Yes, Dfiance is fully cross platform and uses a single player pool for the matchmaking. Players can play on any platform and be matched against players on any other platform.",
					},
				],
			},
			{
				id: "visual-identity",
				title: "Visual Identity",
				qas: [
					{
						question: "Who creates the art?",
						answer:
							"The Art in Dfiance is produced by the Dfiance team and collaborators; styles may evolve over time.",
							
					},
					{
						question: "Is the art made with AI?",
						answer:
							"The great majority of our art is handcrafted by talented human artists – and we proudly support and pay them for their amazing work. You may find Dfiance art in many portfolios on <a href='https://www.artstation.com/dfiance' target='_blank' rel='noopener noreferrer'>Artstation</a>. Any AI art is marked with an Created with AI tag.",
							
					},
					{
						question: "Do you have any recorded videos of the art creation process?",
						answer:
							"Certainly. You may check our <a href='https://www.youtube.com/@dfiance' target='_blank' rel='noopener noreferrer'>Youtube channel</a> for some videos of the art creation process.",
							
					},
					{
						question: "Are the hard copies of the cards available?",
						answer:
							"Yes, we are working on it. We expect to have them available in the near future.",
							
					},
					{
						question: "Is Dfiance available in other languages?",
						answer:
							"Yes, Dfiance is available in several other languages such as Russian, Romanian, Polish, Dutch and others. You can change the language in the settings menu.",
							
					},
				],
			},
			{
				id: "ambassadors",
				title: "Ambassadors",
				qas: [
					{
						question: "Is there an ambassador program?",
						answer:
							"Yes there is. Please reach out to us via <a href='https://discord.gg/fvEN2pMNcE' target='_blank' rel='noopener noreferrer'>Discord</a>.",
							
					},
					{
						question: "I wish to create content for Dfiance, how can I do that?",
						answer:
							"Please reach out to us via <a href='https://discord.gg/fvEN2pMNcE' target='_blank' rel='noopener noreferrer'>Discord</a> and we will do everything we can to support your efforts.",
							
					},
					{
						question: "I wish to translate Dfiance into my language, how can I do that?",
						answer:
							"Please open a ticket on <a href='https://discord.gg/fvEN2pMNcE' target='_blank' rel='noopener noreferrer'>Discord</a>. Please note that we cannot add every language, but we will do our best to support as many as possible.",
							
					},
				],
			},
			{
				id: "team",
				title: "Team",
				qas: [
					{
						question: "How big is the team?",
						answer:
							"It really depends when. We can say that we are a relatively small studio that uses both independent artists as well as in-house ones. The code team includes code writers (devs), ux/ui design, illustrations, marketing, QA, VFX/SFX, game design, 3D modeling and animating, prroject management, operations, devops, lore writing and more. On top of the core team we use a large amount of independent contractors (mainly a lot of extremely talented illustrators and other artists.",
					},
					{
						question: "Where is the team from?",
						answer:
							"The team is from all around the world - Europe, Asia, South America and beyond.",
					},
				],
			},
		],
	},
	{
		id: "game-basics",
		title: "Basics",
		subTopics: [
			{
				id: "what-is-dfiance",
				title: "About the Game",
				qas: [
					{
						question: "What is Dfiance?",
						answer:
							"Dfiance is an online competitive strategy Trading Card Game (TCG), where players face each other in a battle of wits, cunning and strategy.",
					},
					{
						question: "When was Dfiance created?",
						answer:
							"We've been working on Dfiance since 2021, however we had to pivot multiple times to get to where we are today.",
					},
					{
						question: "What are the unique features of Dfiance?",
						answer:
							"Tournaments, Ranked Seasons, Live Spectating, <a href='/faq?what-is-a-golden-deck' rel='noopener noreferrer'>Golden Decks</a> (decks that allow players access to all the cards in the game for practice, friendly challenges and fair tournaments), and more.",
					},
				],
			},
			{
				id: "factions",
				title: "Factions",
				qas: [
					{
						question: "What are the Dfiance factions?",
						answer:
							"There are currently 3 core Dfiance factions: Celestial, Infernal, and Ephemeral. Each has unique themes and strategies. <br><span style='font-style: italic; font-size: 16px;'>A 4th neutral faction, the Factionless, is also available. Up to 15 Factionless cards can be added to any main faction deck to expand the deck's capabilities.</span>",
					},
					{
						question: "Can I mix factions in my deck?",
						answer:
							"Players must use a core faction deck, but can add up to 15 Factionless cards to expand the deck's capabilities.",
					},
					{
						question: "Which faction is the best?",
						answer:
							"That highly depends on the player's skill, luck, play style and the cards they have in their deck. Each faction has its own strengths and weaknesses, and the best faction is the one that the player is most comfortable with.",
					},
				],
			},
			{
				id: "decks-explained",
				title: "Decks",
				qas: [
					{
						question: "What is the Deckbuilder?",
						answer: "The Deckbuilder is the tool used to create and manage your decks and card collection. It allows you to add, remove and edit decks, as well as view your deck's composition and statistics.",
					},
					{
						question: "How many Decks can i have?",
						answer: "You can have up to 4 <strong>Faction decks</strong>, 1 <strong>Golden deck</strong> and 1 <strong>Eternal deck</strong>.<br>Unlocking additional deck slots is a feature that will be available in the future.",
					},
					{
						question: "What is a Faction Deck?",
						answer: "The <strong>Faction Decks</strong> function as your Season / Ranked decks: <br>Can be used to play Ranked, Casual, Training or standard Tournaments. <br>The only decks permitted to participate in Ranked games. <br>You can have different faction decks or 4 same-faction/different-strategy decks. <br> Hold your Season card collection which is reset at the start of each season (while all cards collected during the Season are being saved in your Eternal collection).",
					},
					{
						question: "How to create, edit or delete a deck?",
						answer:
							"Dfiance comes with 3 basic decks, one for each faction. To create a new deck:<bR>Open the game. Click on the <strong>Deckbuilder</strong> button. Create a new deck by clicking on the <strong>'Create Deck'</strong> button.<br>You can edit/customize any deck by clicking on it, then adding and removing cards as you see fit.<br>You can delete a deck by clicking the top-left <strong>Delete Deck</strong> button, visible when editing a deck.",
					},
					{
						question: "What is the Eternal Deck?",
						answer: "The <strong>Eternal Deck</strong> functions the universal deck of your entire Dfiance collection. <br>Unlike Faction Decks, itcan use any of the cards in your collection. For example: At the start of a new Season, your Ranked collection is reset and your Faction decks may only have the default Season start cards; however your Eternal collection will have all the cards you ever collected. <br>The Eternal collection may be used in the Training and Casual modes as well as in Eternal Tournaments and Friendly Challenges.",
					},
					{
						question: "What is a Golden Deck?",
						answer: "The <strong>Golden Deck</strong> is a special unlimited deck that gives you access to all the cards in the game. It is available to all players, and can only be used in Golden Tournaments. This gives an equal chance to all Golden Tournament participants, regardless of their collection size.",
					},
					{
						question: "What are a Deck's limits?",
						answer: "Each deck has the following limits:<br>Max. <strong>5 Unique Heroes</strong>. <br>Max.<strong>15 Factionless</strong> cards.<br>Max. <strong>3 identical Units / Spells copies</strong>. <br>Deck size is <strong>min. 30</strong> and <strong>max. 40</strong> cards.",
					},
					{
						question: "How to navigate a deck?",
						answer: "There are multiple ways to navigate a deck. The quick to access top filter Faction/Row/Type buttons will quickly filter the deck to the desired cards. The Search bar allows you to search for a specific card by name. Use the <strong>Advanced Filter</strong> on the top right bar to open more useful filters.",
					},
					{
						question: "Where can i learn more about decks?",
						answer: "Please visit the community managed <a href='https://dfiance.wiki/decks' target='_blank' rel='noopener noreferrer'>Deck Guide</a> for community created decks. Courtesy of Shackles.",
					},
				],
			},
			{
				id: "getting-started",
				title: "Getting Started",
				qas: [
					{
						question: "How do I start playing?",
						answer:
							"Open the <a href='https://play.dfiance.com' target='_blank' rel='noopener noreferrer'>play.dfiance.com</a> on your Browser or download the Steam client and start playing.",
					},
					{
						question: "Do I need to open an account to play?",
						answer:
							"No. You may play the game as a guest, but you will not be able to save your progress or access your account on other devices.",
					},
					{
						question: "How do I change my player (user) name?",
						answer:
							"You can change your player name (or Username) using one of the following methods:<br>Click on the current name. You should get a pop-up with the option to change your player name.<br>Navigate to <strong>Settings > Account</strong> and use the <strong>Edit Username</strong> button.",
					},
					{
						question: "Are there Quests or Achievements?",
						answer:
							"Dfiance features <strong>Quests</strong>, <strong>Missions</strong> and <strong>Achievements</strong>. <br>Quests are short-term goals that reward you with Gold. You get 3 new Quests every day and can store up to 6 Quests at a time.<br>Missions are Event-term goals that reward you with Special rewards such as skins or special cards. Missions are available for a limited time only.<br>Achievements are long-term goals that reward you with Shards, card packs and other rewards. <br>Quests and Missions can be found in the <strong>Quests</strong> section of the Lobby, while Achievements can be found under the <strong>Achievements</strong> tab on the top right of the Lobby.",
					},
				],
			},
			{
				id: "connect-steam",
				title: "Connect to Steam",
				qas: [
					{
						question: "I started playing on the browser version, how do I migrate to Steam?",
						answer:
							"Connecting your Browser account to your Steam account will result in the <strong>Permanent Removal</strong> of your Steam account and all its progress. Proceeed with caution. To connect your Browser account to your Steam account, follow these steps:<br>Make sure your Steam client is up to date and open.<br>Open the game in the browser.<br>Click on <strong>Settings</strong> icon in the top right corner then go to the <strong>Account</strong> tab.<br>Under the Steam section, Click on the 'Generate Code' button. <strong>Copy the code.</strong><br>Now, open the Steam client. Click on the <strong>Settings</strong> icon and go to the <strong>Account</strong> tab again. Under the Steam section, <strong>paste in the code</strong> and follow the instructions.",
					},
				],
			},
			{
				id: "promo-codes",
				title: "Promo Codes",
				qas: [
					{
						question: "I have a promo code, how do I use it?",
						answer:
							"To use your promo code, open the game in Steam or Browser (process is identical for both). Click on the <strong>Settings</strong> icon in the top right corner then go to the <strong>Account</strong> tab. Under the <strong>Promo Code</strong> section, paste in the code and click on the <strong>Submit</strong> button.",
					},
				],
			},
			{
				id: "player-id",
				title: "Player ID",
				qas: [
					{
						question: "Where can I find my Player ID?",
						answer:
							"Your Player ID is found in the <strong>Settings > Account</strong> at the bottom of the section It is a unique identifier for your account, and can be used to identify you when supporting you.",
					},
				],
			},
		],
	},
	{
		id: "game-rules",
		title: "Gameplay",
		subTopics: [
			{
				id: "rounds",
				title: "General",
				qas: [
					{
						question: "How is Dfiance played?",
						answer:
							"Dfiance is turn-based. Players select a faction, customize their <a href='/faq?decks' target='_blank' rel='noopener noreferrer'>Decks</a> and play against each other in a game of up to 15 turns. Players may play more than 1 card during their turn, depending on their available resources and their cards' play cost.",
					},
					{
						question: "What game modes can I play?",
						answer:
							"You can play Dfiance in <strong>Casual</strong>, <strong>Ranked (Ladder)</strong>, <strong>Tournaments</strong> and <strong>Friendly Challenges (coming soon)</strong>. <br>In Casual mode you can face other players or the AI to practice your skills or if you simply prefer non-competitive play. <br>Ranked games put your skills to the test against other players. You gain ELO for winning Ranked matches, and your ELO drops when losing. The higher your ELO, the higher you climb the Tier ladder (from Bronze and all the way to Titan) and compete for rewards.<br>Tournaments place you against up to 64 other players for significant rewards and glory.<br>Friendly Challenges allow you to face your friends and show off your skills.",
					},
					{
						question: "How do I win a match?",
						answer:
							"You win the game by having higher accumulated ATK (Attack Power) at the end of the game. Check the score on the right side of the screen to see who is winning.",
					},
					{
						question: "What card types are there?",
						answer:
							"There are 3 card types: <strong>Units</strong>, <strong>Spells</strong> and <strong>Heroes</strong>. Units and Heroes can be either Ranged or Melee.<br>Units are limited to 3 copies per deck and have common abilities.<br>Heroes on the other hand, have unique abilities and are limited to 1 copy per deck.<br>Spells are extremely powerful cards that can be used to turn the tide of a game. Spells are spent on use and do not take a slot on the battlefield.",
					},
					{
						question: "What does upgrading a card level do?",
						answer:
							"Upgrading cards simply adds cosmetic effects to the card. It does not affect the card's stats or abilities.",
					},
				],
			},
			{
				id: "phases",
				title: "Game Phases",
				qas: [
					{
						question: "What are a game's Phases?",
						answer:
							"Once you click Find Battle the game will starting looking for an opponent. When one is found a game begins:<br>The First phase is the <strong>Re-Draw (Mulligan)</strong> Phase. At the start of the game, you automatically draw 6 random cards from your deck to your hand, and you may choose up to 3 cards (1 by 1) to discard and replace (redraw from your deck).<br>The Second phase is the <strong>Play Phase (Turns 1-10)</strong>. You play cards from your hand, and use your <a href='/faq?resources' rel='noopener noreferrer'>Resources</a> to play them.<br>The Third phase is the <strong>Last Stand </strong>Phase. Starting Turn 10 and up to turn 15, each player <strong>must LEAD the score</strong> at the end of their turn or else they loses the battle (a draw will result in a loss). Leading the score at the end of the turn does not guarantee a win, but rather forces the opponent to take the lead or lose the battle.<br>The Fourth and last phase is the <strong>Battle End</strong> Phase, end-game summary and return to Lobby.",
					},
					{
						question: "Expand on the Mulligan Phase?",
						answer:
							"Understanding how the Mulligan Phase works can be key to winning games. When you select a card to discard:<br>Discarded cards cannot be re-drawn during the Mulligan Phase.<br>You are also discarding <strong>all copies of that card</strong>.<br>At the end of the Mulligan Phase, all discarded <strong>cards are returned to your deck</strong> and the deck is shuffled.",
					},
					{
						question: "Expand on the Play Phase?",
						answer:
							"The Play phase runs from <strong>Turn 1 to Turn 10</strong>. The player that goes first is selected randomly at the start of the game and is marked with a flag near his avatar picture on the right side of the screen.<br>Turn time limit: <strong>50 seconds</strong> in Ranked and Casual games. <strong>40 seconds</strong> in Tournament games.<br>Unused Turn time is deposited into your <strong>Time Bank</strong>, which can store up to <strong>50 seconds</strong> of unused time and is automatically triggered when you run out of time (especially relevant at later Turns).<br>At the start of each Turn, you receive <strong>3 Action Points (AP)</strong> and <strong>1 Mana Point (MP)</strong>.",
					},
					{
						question: "Expand on the Last Stand Phase?",
						answer:
							"The Last Stand phase runs from <strong>Turn 10 and up to Turn 15</strong>.<br>Lead the score at the end of your Turn or you lose the game. If you lead the score, the Turn is passed to your opponent and he must lead the score at the end of his Turn or he loses the game. If he is leading the score, the Turn is passed back to you and so on.<br>Draws are considered losses.<br>If the game reaches Turn 15, it will automatically resolve based on the current score.",
					},
					{
						question: "How do I know who's Turn it is?",
						answer:
							"The player that goes first every Turn is marked with a flag by his profile picture (center right of the screen). For example, if it is now Turn 5 and the opponent is playing and he is marked with a flag, the next turn will be your Turn 5.",
					},
				],
			},
			{
				id: "resources",
				title: "Resources",
				qas: [
					{
						question: "What are Action Points (AP)?",
						answer:
							"Action Points (AP) is the resource that you use to play Unit or Hero-type cards. You start with 3 AP and gain 3 AP per turn. Each Unit or Hero card has a cost in AP, and you can play as many cards as you have AP.",
					},
					{
						question: "What are Mana Points (MP)?",
						answer:
							"Mana Points (MP) is the resource that you use to play Spell-type cards. You start with 1 MP and gain 1 MP per turn. Each Spell card has a cost in MP, and you can play as many cards as you have MP.",
					},
					{
						question: "What is Gold used for?",
						answer:
							"Gold is used for buying cards from the Shop. You can also use it to buy cards from other players on the <strong>Marketplace</strong>.",
					},
					{
						question: "What are Shards?",
						answer:
							"Shards are the premium currency of Dfiance, and can be used to purchase Cosmetics for your collection.You can earn Shards by playing games, completing Quests and Achievementsand participating in Tournaments.",
					},
				],
			},
			{
				id: "sections",
				title: "Battlefield",
				qas: [
					{
						question: "How is a battlefield divided?",
						answer:
							"For each player, the battlefield is divided into 4 sections (or rows): <strong>Left Flank</strong>, <strong>Melee</strong>, <strong>Right Flank</strong> and <strong>Ranged rows</strong>. Your sections are opposite to your opponent's sections.",
					},
					{
						question: "Can I play any card on any row?",
						answer:
							"No. Melee (Brawler) cards may be played on Melee or Flank rows while Ranged cards may be played on Ranged and Flank rows. You cannot play a Melee card on a Ranged row and vice versa (at least not without using special abilities).",
					},
					{
						question: "How many cards can I fit per row?",
						answer:
							"Each row can hold a different number of cards, depending on the row's type:<br>Melee rows can hold up to 5 cards.<br>Flank rows can hold up to 2 cards.<br>Ranged rows can hold up to 7 cards.",
					},
					{
						question: "What function does my Avatar have?",
						answer:
							"Your Avatar is mainly cosmetic, but it can also be used to: <br>Use the <strong>Leader Ability</strong>.<br>Communicate with your opponent using Stickers or Animations from the Chat wheel.",
					},
					{
						question: "What is the Graveyard?",
						answer:
							"The Graveyard (aka the Discard Pile), is where your cards go when they have been destroyed (either by your opponenet or by your own abilities) or used (e.g spells). You can see the cards in your Graveyard by clicking the Graveyard of the battlefield.<br>You can view your opponent's Graveyard by clicking the Graveyard of the battlefield.<br>Cards can be permanently removed from the Graveyard using special abilities such as <strong>Banish</strong>.",
					},
					{
						question: "Can I see the cards in my Deck on the battlefield?",
						answer:
							"Yes. You can see your deck on the battlefield by clicking the Deck (bottom left) of the battlefield.",
					},
					{
						question: "What is the Time Bank?",
						answer:
							"The Time Bank holds up to <strong>50 seconds</strong> of unused time from your Turns. <br>When you run out of time, the Time Bank is automatically triggered and you are given the time you have left in the Bank.<br>The Time Bank is automatically refilled with any unused time in following Turns.",
					},
				],
			},
			{
				id: "tactical-points",
				title: "Tactical Points",
				qas: [
					{
						question: "What are Tactical Advantage Points (TAP)?",
						answer:
							"Tactical Advantage Points (AKA Tactical Points) are permanent game points that players gain when for each of the 4 <a href='/faq?sections' rel='noopener noreferrer'>Sections</a> of the board (Left Flank, Melee, Right Flank and Ranged sections or rows) that they control at the start of THEIR turn.<br>In a standard game, a maximum of 4 Tactical Points can be earned per turn.<br>To control a section, a player must have more accumulated ATK (attack power) than the opponent in that section.<br>Hovering over a section will show you the accumulated ATK for each player in that section and the current section controller will be marked with a crown icon.",
					},
					{
						question: "Are there abilities that affect Tactical Points?",
						answer:
							"Many cards affect Tactical Points, such as the abilities of <a href='https://www.dfiance.wiki/cards/ephemeral/governor-menonsen' target='_blank' rel='noopener noreferrer'><strong>Governor Menonsen</strong></a> or <a href='https://www.dfiance.wiki/cards/infernal/shanharash' target='_blank' rel='noopener noreferrer'><strong>Shanharash</strong></a>.",
					},
					{
						question: "What are Tactical Tournaments?",
						answer:
							"Tactical Tournamens have a Tactical Points <a href='/faq?what-are-tournament-modifiers' rel='noopener noreferrer'>modifier</a> that applies, meaning that each section grants more than 1 TAP per turn (check the Tournamnet modfier section for the exact amount). Plan your strategy accordingly.",
					},
					{
						question: "Do Tactical Points affect the outcome of a game?",
						answer:
							"Absolutely. Tactical Points are a key factor in the outcome of a game. A player with more Tactical Points will have a significant advantage over their opponent.",
					},
				],
			},
			{
				id: "tournaments",
				title: "Tournaments",
				qas: [
					{
						question: "What are Tournaments?",
						answer:
							"Tournaments place up to 64 players in a contest against each other. If 32 players are participating, the first round will include 16 matches, the following one 8 and so on.<br>The winner of each match is moves up to the next round, and the loser is considered a dropout.",
					},
					{
						question: "What are Tournament Modifiers?",
						answer:
							"Modifiers are special rules that apply to a Tournament. They can be used to make the Tournament more challenging or to make it more rewarding. For example, a tactical tournament modifier will grant you additional Tactical Points per turn for areas you control.",
					},
					{
						question: "How many players can win a Tournament?",
						answer:
							"Tournaments have rewards for 1st, 2nd, 3rd place and 4th place. <br>The more players participate, the more rewards are available.<br>Dropouts are also rewarded for participating.",
					},
					{
						question: "What are Golden Tournaments?",
						answer:
							"Tournamnets that use the special <a href='/faq?what-is-a-golden-deck' rel='noopener noreferrer'>Golden Deck</a> modifier. Golden Tournaments provide an equally fair chance for all players to win, regardless of their existing collection.",
					},
					{
						question: "How can I participate in a Tournament?",
						answer:
							"Signup for a Tournament from the Tournaments section accessible from the Lobby. <br>Each Tournament may have different signup fees, modifiers and prize pools.",
					},
					{
						question: "Is it possible to spectate a Tournament?",
						answer:
							"Yes, Tournament matches can be spectated by clicking the spectator button next to the match in the Tournament lobby.<br> You will not be able to spectate if you are actively playing in the Tournament until you drop out.<br>Spectating matches may include some intentional delay.<br>Spectators may switch between player views while spectating.<br>Player in-hand cards are hidden from spectators.",
					},
					{
						question: "Do Tournaments impact my ELO?",
						answer:
							"Tournaments will currently not impact your ELO.",
					},
				],
			},
		],
	},
	{
		id: "rules-and-guidelines",
		title: "Fair Play Rules",
		subTopics: [
			{
				id: "rules-accounts",
				title: "Accounts",
				qas: [
					{
						question: "Am I allowed to have more than 1 account?",
						answer:
							"Players may play more than 1 account, however, trading between your own accounts is strictly forbidden and punishable by a permanent ban.",
					},
					{
						question: "Can I share my account with others?",
						answer:
							"No. Accounts are personal and non-transferable. Beyond security concerns, account sharing is associated with activities like boosting, which undermines the integrity of ranked play and creates an unfair competitive environment. Sharing your account with others is therefore strictly forbidden and punishable by a permanent ban or other disciplinary actions.",
					}
				],
			},
			{
				id: "rules-reporting",
				title: "Reporting",
				qas: [
					{
						question: "I have found a bug or a glitch in the game, what should I do?",
						answer:
							"Please report the bug or glitch by clicking the report button in the game. You can also report it by opening a ticket in our <a href='https://discord.gg/fvEN2pMNcE' target='_blank' rel='noopener noreferrer'>Discord</a>.",
					},
					{
						question: "Am I allowed to use game bugs or glitches to my advantage?",
						answer:
							"Absolutely not. Using game bugs or glitches to your advantage is strictly forbidden. Please report immediately.",
					},
				],
			},
			{
				id: "rules-community",
				title: "Community",
				qas: [
					{
						question: "Is there a Dfiance community forum?",
						answer:
							"Yes. Please join our community in our <a href='https://discord.gg/fvEN2pMNcE' target='_blank' rel='noopener noreferrer'>Discord</a>.",
					},
					{
						question: "Are there any community rules?",
						answer:
							"Absolutely.<br>No harassment, spamming, trolling, or any other form of disruptive behavior is allowed.<br>All community members are expected to be respectful and courteous to each other, especially when in disagreement.",
					},
					{
						question: "Can I provide the team with feedback or suggestions?",
						answer:
							"Constructive feedback and suggestions are always welcome. If you have a suggestion, please use the suggestion section in the Discord.",
					},
				],
			},
		],
	},
];

const stripHtml = (s: string) => s.replace(/<[^>]*>/g, " ");

// Helper function to generate URL-friendly IDs from question text
const generateQuestionId = (question: string): string => {
	return question
		.toLowerCase()
		.replace(/[^a-z0-9\s]/g, '') // Remove special characters
		.replace(/\s+/g, '-') // Replace spaces with hyphens
		.replace(/-+/g, '-') // Replace multiple hyphens with single
		.replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

// Helper function to find FAQ item by question ID
const findFaqByQuestionId = (questionId: string, data: Topic[]): { topic: Topic; subTopic: SubTopic; qaIndex: number } | null => {
	for (const topic of data) {
		for (const subTopic of topic.subTopics) {
			const qaIndex = subTopic.qas.findIndex(qa => generateQuestionId(qa.question) === questionId);
			if (qaIndex !== -1) {
				return { topic, subTopic, qaIndex };
			}
		}
	}
	return null;
};

// Helper function to find sub-topic by ID
const findSubTopicById = (subTopicId: string, data: Topic[]): { topic: Topic; subTopic: SubTopic } | null => {
	for (const topic of data) {
		for (const subTopic of topic.subTopics) {
			if (generateQuestionId(subTopic.title) === subTopicId || subTopic.id === subTopicId) {
				return { topic, subTopic };
			}
		}
	}
	return null;
};

export default function FAQ() {
	const location = useLocation();
	const navigate = useNavigate();
	const [query, setQuery] = useState("");
	const [noResults, setNoResults] = useState<string | null>(null);
	const [highlightedId, setHighlightedId] = useState<string | null>(null);
	const [activeTopicId, setActiveTopicId] = useState<string | null>(faqData[0]?.id ?? null);
	const [activeSubId, setActiveSubId] = useState<string | null>(null);
	const pinnedSubIdRef = useRef<string | null>(null);
	const topicNavLockRef = useRef<string | null>(null);
	const topicNavUnlockTimerRef = useRef<number | null>(null);
	const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
	const [isMobileNavCollapsed, setIsMobileNavCollapsed] = useState<boolean>(true);
	const [searchHighlight, setSearchHighlight] = useState<string | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const [highlightedQuestionId, setHighlightedQuestionId] = useState<string | null>(null);
	const [searchMatches, setSearchMatches] = useState<Array<{topicId: string; subId: string; topicTitle: string; subTitle: string}>>([]);
	const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(0);

	// Sticky layout refs/state
	const pageRef = useRef<HTMLDivElement | null>(null);
	const headerRef = useRef<HTMLDivElement | null>(null);
	const subnavRef = useRef<HTMLDivElement | null>(null);
	const bodyRef = useRef<HTMLDivElement | null>(null);
	const sidenavRef = useRef<HTMLElement | null>(null);

	// Helper: escape regex
	const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	// Helper: highlight occurrences of term within HTML by wrapping text nodes with <mark>
	const highlightHtml = useCallback((html: string, term: string) => {
		if (!term) return html;
		const container = document.createElement("div");
		container.innerHTML = html;
		const rx = new RegExp(escapeRegExp(term), "gi");
		const walk = (node: Node) => {
			if (node.nodeType === Node.TEXT_NODE) {
				const text = node.nodeValue || "";
				if (!rx.test(text)) return;
				const frag = document.createDocumentFragment();
				let last = 0;
				text.replace(rx, (match, offset) => {
					if (offset > last) frag.appendChild(document.createTextNode(text.slice(last, offset)));
					const mark = document.createElement("mark");
					mark.className = "faq-search-hl";
					mark.textContent = match;
					frag.appendChild(mark);
					last = offset + match.length;
					return match;
				});
				if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
				node.parentNode && node.parentNode.replaceChild(frag, node);
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				const children = Array.from((node as Element).childNodes);
				children.forEach(walk);
			}
		};
		walk(container);
		return container.innerHTML;
	}, []);

	const [headerFixed, setHeaderFixed] = useState(false);
	const [sidenavFixed, setSidenavFixed] = useState(false);

	const [headerHeight, setHeaderHeight] = useState(0);
	const [subnavHeight, setSubnavHeight] = useState(0);
	const [pageLeftVp, setPageLeftVp] = useState(0);
	const [pageWidth, setPageWidth] = useState(0);
	const [bodyLeftVp, setBodyLeftVp] = useState(0);
	const [sidenavHeight, setSidenavHeight] = useState<number | undefined>(undefined);
	const [isDesktop, setIsDesktop] = useState<boolean>(true);
	const subnavTopDoc = useRef<number>(0);
	const sidenavTopDoc = useRef<number>(0);

	const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const topicRefs = useRef<Record<string, HTMLElement | null>>({});

	const updateCssVars = useCallback(() => {
		const root = pageRef.current;
		if (!root) return;
		const h = headerRef.current?.offsetHeight ?? headerHeight;
		const s = subnavRef.current?.offsetHeight ?? 0;
		root.style.setProperty("--faq-header-h", `${h}px`);
		root.style.setProperty("--faq-subnav-h", `${s}px`);
	}, [headerHeight]);

	const flatIndex = useMemo(
		() =>
			faqData.flatMap(topic =>
				topic.subTopics.map(sub => ({
					topicId: topic.id,
					topicTitle: topic.title,
					subId: sub.id,
					subTitle: sub.title,
					text: `${topic.title} ${sub.title} ${sub.qas
						.map(q => `${stripHtml(q.question)} ${stripHtml(q.answer)}`)
						.join(" ")}`.toLowerCase(),
				})),
			),
		[],
	);

	const handleSearch = useCallback(
		(e?: React.FormEvent) => {
			e && e.preventDefault();
			const normalized = query.trim().toLowerCase();
			if (!normalized) {
				setNoResults(null);
				setSearchHighlight(null);
				setSearchMatches([]);
				setCurrentMatchIndex(0);
				return;
			}
			
			// Find all matches instead of just the first one
			const matches = flatIndex.filter(item => item.text.includes(normalized));
			
			if (matches.length > 0) {
				setSearchMatches(matches);
				
				// If we already have matches for this query, cycle to the next one
				let nextIndex = 0;
				if (searchMatches.length > 0 && searchHighlight === normalized) {
					nextIndex = (currentMatchIndex + 1) % matches.length;
				}
				setCurrentMatchIndex(nextIndex);
				
				const match = matches[nextIndex];
				const el = sectionRefs.current[match.subId];
				if (el) {
					pinnedSubIdRef.current = match.subId;
					const heading = el.querySelector('.faq-subtopic-title') as HTMLElement | null;
					(heading ?? el).scrollIntoView({ behavior: "smooth", block: "start" });
					setHighlightedId(match.subId);
					setActiveTopicId(match.topicId);
					setActiveSubId(match.subId);
					setSearchHighlight(normalized);
					setTimeout(() => setHighlightedId(null), 1600);
					setNoResults(null);
				}
			} else {
				setNoResults(`No results for: "${query}"`);
				setSearchHighlight(null);
				setSearchMatches([]);
				setCurrentMatchIndex(0);
			}
		}, [flatIndex, query, searchMatches, currentMatchIndex, searchHighlight],
	);

	// Clear highlight on nav/input interactions
	const clearSearchUi = useCallback(() => {
		setQuery("");
		setNoResults(null);
		setSearchHighlight(null);
		setSearchMatches([]);
		setCurrentMatchIndex(0);
	}, []);

	// Function to copy question link to clipboard
	const copyQuestionLink = useCallback(async (questionId: string) => {
		try {
			const url = `${window.location.origin}${location.pathname}?${questionId}`;
			await navigator.clipboard.writeText(url);
			setCopiedId(questionId);
			setTimeout(() => setCopiedId(null), 2000);
		} catch (err) {
			console.error('Failed to copy link:', err);
		}
	}, [location.pathname]);

	// Function to copy section link to clipboard
	const copySectionLink = useCallback(async (sectionId: string) => {
		try {
			const url = `${window.location.origin}${location.pathname}?${sectionId}`;
			await navigator.clipboard.writeText(url);
			setCopiedId(sectionId);
			setTimeout(() => setCopiedId(null), 2000);
		} catch (err) {
			console.error('Failed to copy link:', err);
		}
	}, [location.pathname]);

	// Handle URL parameters on component mount
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const paramValue = urlParams.keys().next().value; // Get first parameter key
		
		if (paramValue) {
			// First try to find a specific question
			const faqItem = findFaqByQuestionId(paramValue, faqData);
			if (faqItem) {
				// Navigate to the specific question
				const { topic, subTopic } = faqItem;
				setActiveTopicId(topic.id);
				setActiveSubId(subTopic.id);
				
				// Scroll to the subtopic after a brief delay to ensure DOM is ready
				setTimeout(() => {
					const el = sectionRefs.current[subTopic.id];
					if (el) {
						pinnedSubIdRef.current = subTopic.id;
						// Always scroll to the container element (which has the border) not the heading
						el.scrollIntoView({ behavior: "smooth", block: "start" });
						setHighlightedId(subTopic.id);
						setTimeout(() => setHighlightedId(null), 1600);
						
						// Highlight the specific question for 3 seconds
						setHighlightedQuestionId(paramValue);
						setTimeout(() => setHighlightedQuestionId(null), 3000);
					}
				}, 100);
			} else {
				// If not a question, try to find a sub-topic
				const subTopicItem = findSubTopicById(paramValue, faqData);
				if (subTopicItem) {
					const { topic, subTopic } = subTopicItem;
					setActiveTopicId(topic.id);
					setActiveSubId(subTopic.id);
					
					// Scroll to the subtopic after a brief delay to ensure DOM is ready
					setTimeout(() => {
						const el = sectionRefs.current[subTopic.id];
						if (el) {
							pinnedSubIdRef.current = subTopic.id;
							// Always scroll to the container element (which has the border) not the heading
							el.scrollIntoView({ behavior: "smooth", block: "start" });
							setHighlightedId(subTopic.id);
							setTimeout(() => setHighlightedId(null), 1600);
						}
					}, 100);
				}
			}
		}
	}, [location.search]);

	// Determine active topic based on scroll position
	useEffect(() => {
		const topicObserver = new IntersectionObserver(
			entries => {
				// Suppress updates while a topic nav lock is active
				if (topicNavLockRef.current) return;
				const visible = entries
					.filter(e => e.isIntersecting)
					.sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];
				if (visible && visible.target instanceof HTMLElement) {
					setActiveTopicId(visible.target.id);
				}
			},
			{ root: null, rootMargin: "-120px 0px -40% 0px", threshold: [0.1, 0.25, 0.5, 0.75] },
		);

		faqData.forEach(topic => {
			const el = topicRefs.current[topic.id];
			if (el) topicObserver.observe(el);
		});
		return () => topicObserver.disconnect();
	}, []);

	// Determine active subtopic based on scroll position
	useEffect(() => {
		// Use different settings for mobile vs desktop
		const isMobileDevice = window.innerWidth < 1024;
		const rootMargin = isMobileDevice 
			? "-80px 0px -50% 0px"  // Less aggressive margins for mobile
			: "-120px 0px -60% 0px"; // Original desktop margins
		
		const subObserver = new IntersectionObserver(
			entries => {
				// Update active subtopic based on visibility, unless a subtopic is pinned by programmatic nav
				const visible = entries
					.filter(e => e.isIntersecting)
					.sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];
				if (visible && visible.target instanceof HTMLElement) {
					if (pinnedSubIdRef.current && pinnedSubIdRef.current !== visible.target.id) return;
					setActiveSubId(visible.target.id);
					// Keep the active topic synchronized with the visible subtopic during natural scroll
					if (!topicNavLockRef.current) {
						const parent = faqData.find(t => t.subTopics.some(s => s.id === visible.target.id));
						if (parent) setActiveTopicId(parent.id);
					}
				}
			},
			{ 
				root: null, 
				rootMargin: rootMargin, 
				threshold: isMobileDevice ? [0.1, 0.3, 0.5] : [0.25, 0.5, 0.75] // Lower thresholds for mobile
			},
		);

		Object.values(sectionRefs.current).forEach(el => {
			if (el) subObserver.observe(el);
		});
		return () => subObserver.disconnect();
	}, [isDesktop]); // Re-run when switching between mobile/desktop

	const scrollToTopic = useCallback((id: string) => {
		const el = topicRefs.current[id];
		if (el) {
			const heading = el.querySelector('.faq-topic-title') as HTMLElement | null;
			(heading ?? el).scrollIntoView({ behavior: "smooth", block: "start" });
			setActiveTopicId(id);
			// Lock topic updates until we reach the target or timeout
			topicNavLockRef.current = id;
			if (topicNavUnlockTimerRef.current) window.clearTimeout(topicNavUnlockTimerRef.current);
			topicNavUnlockTimerRef.current = window.setTimeout(() => { topicNavLockRef.current = null; }, 1500);
			clearSearchUi();
		}
	}, [clearSearchUi]);

	const scrollToSub = useCallback((id: string) => {
		const el = sectionRefs.current[id];
		if (el) {
			pinnedSubIdRef.current = id;
			// Lock topic to the parent during subtopic navigation to prevent flicker
			const parent = faqData.find(t => t.subTopics.some(s => s.id === id));
			if (parent) {
				setActiveTopicId(parent.id);
				topicNavLockRef.current = parent.id;
				if (topicNavUnlockTimerRef.current) window.clearTimeout(topicNavUnlockTimerRef.current);
				topicNavUnlockTimerRef.current = window.setTimeout(() => { topicNavLockRef.current = null; }, 1500);
			}
			// Always scroll to the container element (which has the border) not the heading
			el.scrollIntoView({ behavior: "smooth", block: "start" });
			setActiveSubId(id);
			setHighlightedId(id);
			setTimeout(() => setHighlightedId(null), 1600);
			setTimeout(() => {
				try { el.focus({ preventScroll: true } as FocusOptions); } catch (_err) { /* noop */ }
			}, 0);
			clearSearchUi();
		}
	}, [clearSearchUi]);

	// Function to navigate to a specific question by its ID and update URL
	const scrollToQuestion = useCallback((questionId: string) => {
		const faqItem = findFaqByQuestionId(questionId, faqData);
		if (faqItem) {
			const { topic, subTopic } = faqItem;
			setActiveTopicId(topic.id);
			setActiveSubId(subTopic.id);
			scrollToSub(subTopic.id);
			
			// Update URL without causing a page refresh
			const newUrl = `${location.pathname}?${questionId}`;
			navigate(newUrl, { replace: true });
			
			// Highlight the specific question for 3 seconds
			setHighlightedQuestionId(questionId);
			setTimeout(() => setHighlightedQuestionId(null), 3000);
		}
	}, [location.pathname, navigate, scrollToSub]);

	// Function to navigate to a specific sub-topic by its ID and update URL
	const scrollToSubTopic = useCallback((subTopicId: string) => {
		const subTopicItem = findSubTopicById(subTopicId, faqData);
		
		if (subTopicItem) {
			const { topic, subTopic } = subTopicItem;
			setActiveTopicId(topic.id);
			setActiveSubId(subTopic.id);
			
			// Update URL without causing a page refresh FIRST
			const newUrl = `${location.pathname}?${subTopicId}`;
			navigate(newUrl, { replace: true });
			
			// Then scroll to the element after a small delay
			setTimeout(() => {
				const el = sectionRefs.current[subTopic.id];
				if (el) {
					pinnedSubIdRef.current = subTopic.id;
					// Always scroll to the container element (which has the border) not the heading
					el.scrollIntoView({ behavior: "smooth", block: "start" });
					setHighlightedId(subTopic.id);
					setTimeout(() => setHighlightedId(null), 1600);
				}
			}, 50);
		}
	}, [location.pathname, navigate]);

	// While navigating to a topic, clear the lock when the heading aligns under the header
	useEffect(() => {
		const onScroll = () => {
			const targetId = topicNavLockRef.current;
			if (!targetId) return;
			const topicEl = topicRefs.current[targetId];
			if (!topicEl) return;
			const heading = topicEl.querySelector('.faq-topic-title') as HTMLElement | null;
			if (!heading) return;
			const targetTop = (headerRef.current?.offsetHeight ?? headerHeight) + 12;
			const currentTop = heading.getBoundingClientRect().top;
			if (Math.abs(currentTop - targetTop) <= 3) {
				// Aligned: release the lock now
				topicNavLockRef.current = null;
				if (topicNavUnlockTimerRef.current) {
					window.clearTimeout(topicNavUnlockTimerRef.current);
					topicNavUnlockTimerRef.current = null;
				}
			}
		};
		window.addEventListener('scroll', onScroll, { passive: true } as AddEventListenerOptions);
		return () => window.removeEventListener('scroll', onScroll as EventListener);
	}, [headerHeight]);

	// Clear pinned subtopic on user interactions, so observer can resume tracking
	useEffect(() => {
		const clearPin = () => { pinnedSubIdRef.current = null; };
		let touchTimer: number | null = null;
		
		// On mobile, delay clearing the pin to avoid interference with scroll detection
		const clearPinMobile = () => {
			if (touchTimer) clearTimeout(touchTimer);
			touchTimer = window.setTimeout(() => {
				pinnedSubIdRef.current = null;
			}, 500); // Wait 500ms after touch before clearing
		};
		
		const isMobileDevice = window.innerWidth < 1024;
		
		window.addEventListener('wheel', clearPin, { passive: true } as AddEventListenerOptions);
		window.addEventListener('touchstart', isMobileDevice ? clearPinMobile : clearPin, { passive: true } as AddEventListenerOptions);
		window.addEventListener('keydown', clearPin);
		
		return () => {
			if (touchTimer) clearTimeout(touchTimer);
			window.removeEventListener('wheel', clearPin as EventListener);
			window.removeEventListener('touchstart', (isMobileDevice ? clearPinMobile : clearPin) as EventListener);
			window.removeEventListener('keydown', clearPin as EventListener);
		};
	}, []);

	const measureLayout = useCallback(() => {
		const pageEl = pageRef.current;
		const headerEl = headerRef.current;
		const subnavEl = subnavRef.current;
		const sidenavEl = sidenavRef.current;
		const bodyEl = bodyRef.current;

		if (pageEl) {
			const r = pageEl.getBoundingClientRect();
			setPageLeftVp(r.left);
			setPageWidth(r.width);
		}
		if (bodyEl) {
			const r = bodyEl.getBoundingClientRect();
			setBodyLeftVp(r.left);
		}
		if (headerEl) {
			setHeaderHeight(headerEl.offsetHeight);
		}
		if (subnavEl) setSubnavHeight(subnavEl.offsetHeight);
		if (subnavEl) subnavTopDoc.current = subnavEl.getBoundingClientRect().top + window.scrollY;
		if (sidenavEl) {
			const r = sidenavEl.getBoundingClientRect();
			sidenavTopDoc.current = r.top + window.scrollY;
			setSidenavHeight(sidenavEl.offsetHeight);
		}
		setIsDesktop(window.innerWidth >= 1024);
		// Sync CSS vars for accurate scroll-margin offsets
		requestAnimationFrame(updateCssVars);
	}, []);

	const updateSticky = useCallback(() => {
		const pageEl = pageRef.current;
		if (!pageEl) return;
		const pageTop = pageEl.getBoundingClientRect().top;
		const shouldFixHeader = pageTop <= 0;
		setHeaderFixed(shouldFixHeader);


		// Keep CSS vars in sync while scrolling/resizing
		updateCssVars();

			// Always fix the sidebar so it remains visible while scrolling
			setSidenavFixed(true);
		}, [headerHeight, isDesktop, updateCssVars]);

	useEffect(() => {
		measureLayout();
		updateSticky();
		const onScroll = () => {
			updateSticky();
			setShowScrollTop(window.scrollY > 400);
		};
		const onResize = () => {
			measureLayout();
			updateSticky();
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onResize);
		return () => {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onResize);
		};
	}, [measureLayout, updateSticky]);

	const scrollToPageTop = useCallback(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	// Inline styles when fixed
	const headerFixedStyle = headerFixed
		? { position: "fixed" as const, top: 0, left: pageLeftVp, width: pageWidth, zIndex: 1000 }
		: undefined;
	const sidenavFixedStyle = sidenavFixed
		? {
			position: "fixed" as const,
			top: headerHeight + subnavHeight + 12,
			left: bodyLeftVp,
			width: (isDesktop ? 240 : (isMobileNavCollapsed ? 28 : 168)),
			zIndex: 998,
		}
		: undefined;

	return (
		<>
		<div className="faq-page" ref={pageRef}>
			{/* Sticky header with search */}
			<div className="faq-header" ref={headerRef} style={headerFixedStyle}>
				<Link className="faq-header-home" to="/" aria-label="Back to home" title="Home">
					<svg viewBox="0 0 24 24" aria-hidden>
						<path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor" />
					</svg>
				</Link>
				<div className="faq-logo-center" aria-hidden>
					<img src={Logo} alt="Dfiance" className="faq-logo-img mobile-only" />
					<img src={Logo} alt="Dfiance" className="faq-logo-img desktop-only" />
				</div>
				<form className="faq-search" onSubmit={handleSearch} role="search">
					<input
						className="faq-search-input"
						placeholder="Search keywords..."
						value={query}
						onChange={e => {
							setQuery(e.target.value);
							// Reset search state when query changes
							if (e.target.value.trim().toLowerCase() !== searchHighlight) {
								setSearchMatches([]);
								setCurrentMatchIndex(0);
							}
						}}
						onClick={() => { 
							setQuery(""); 
							setNoResults(null); 
							setSearchMatches([]);
							setCurrentMatchIndex(0);
						}}
						aria-label="Search FAQ"
					/>
					<button className="faq-search-btn" type="submit" aria-label={searchMatches.length > 0 ? "Next match" : "Search"}>
						<span className="faq-search-btn-inner">
							<svg className="faq-search-svg" viewBox="0 0 24 24" aria-hidden>
								<circle cx="10.5" cy="10.5" r="6.5" />
								<path d="M15 15l6 6" />
							</svg>
							<span className="faq-search-text">
								{searchMatches.length > 0 ? "Next" : "Search"}
								{searchMatches.length > 1 && (
									<span className="faq-search-counter"> ({currentMatchIndex + 1}/{searchMatches.length})</span>
								)}
							</span>
						</span>
					</button>
				</form>
				<div className="faq-header-status" aria-live="polite">
					{noResults ? <p className="faq-search-empty" role="status">{noResults}</p> : null}
				</div>
			</div>
			{headerFixed && <div style={{ height: headerHeight }} />}

			{/* Body with sidebar and content */}
			<div className={`faq-body ${!isDesktop && isMobileNavCollapsed ? "collapsed" : "expanded"}`} ref={bodyRef}>
				<aside className={`faq-sidenav ${!isDesktop && isMobileNavCollapsed ? "collapsed" : ""}`} aria-label="Topics" ref={sidenavRef} style={sidenavFixedStyle}>
					<button
						className="faq-sidenav-toggle"
						onClick={() => setIsMobileNavCollapsed(prev => !prev)}
						aria-label={isMobileNavCollapsed ? "Expand navigation" : "Collapse navigation"}
						aria-expanded={!isMobileNavCollapsed}
					>
						{isMobileNavCollapsed ? "›" : "‹"}
					</button>
					<nav className="faq-sidenav-nav">
						{faqData.map(topic => (
							<div key={topic.id} className="faq-sidenav-group">
								<button
									className={`faq-sidenav-link ${activeTopicId === topic.id ? "active" : ""}`}
									onClick={() => scrollToTopic(topic.id)}
								>
									{topic.title}
								</button>
								{activeTopicId === topic.id && (
									<div className="faq-sidenav-subnav">
										{topic.subTopics.map((sub: SubTopic) => (
											<button
												key={sub.id}
												className={`faq-sidenav-sublink ${activeSubId === sub.id ? "active" : ""}`}
												onClick={() => { 
													setActiveTopicId(topic.id); 
													scrollToSubTopic(generateQuestionId(sub.title));
												}}
											>
												{sub.title}
											</button>
										))}
									</div>
								)}
							</div>
						))}
					</nav>
				</aside>
				<div
					className="faq-sidenav-placeholder"
					style={{ width: (isDesktop ? 240 : (isMobileNavCollapsed ? 28 : 168)), height: sidenavHeight, gridColumn: "1 / 2" }}
				/>

				{/* Content */}
				<div className="faq-content">
					{faqData.map(topic => (
						<section className="faq-topic" id={topic.id} key={topic.id} ref={el => (topicRefs.current[topic.id] = el)}>
							<h2 className="faq-topic-title">{topic.title}</h2>
							{topic.subTopics.map(sub => (
								<div
									key={sub.id}
									id={sub.id}
									ref={el => (sectionRefs.current[sub.id] = el)}
									tabIndex={-1}
									className={`faq-subtopic ${highlightedId === sub.id ? "highlight" : ""}`}
								>
									<div className="faq-subtopic-header">
										<h3 
											className="faq-subtopic-title"
											onClick={() => scrollToSubTopic(generateQuestionId(sub.title))}
											style={{ cursor: 'pointer' }}
											title="Click to get direct link to this section"
										>
											{sub.title}
										</h3>
										<button
											className="faq-link-btn faq-section-link-btn"
											onClick={(e) => {
												e.stopPropagation();
												copySectionLink(generateQuestionId(sub.title));
											}}
											title="Copy direct link to this section"
											aria-label="Copy section link"
										>
											{copiedId === generateQuestionId(sub.title) ? (
												<span className="faq-link-icon faq-link-copied">✓</span>
											) : (
												<span className="faq-link-icon">🔗</span>
											)}
										</button>
									</div>
									<dl className="faq-qa-list">
										{sub.qas.map((qa, idx) => {
											const parts = qa.answer.split(/<br\s*\/?>(?!\s*<)/gi);
											const questionId = generateQuestionId(qa.question);
											return (
												<div className={`faq-qa ${highlightedQuestionId === questionId ? 'faq-qa-highlighted' : ''}`} key={`${sub.id}-qa-${idx}`}>
													<dt className="faq-q-wrapper">
														<span 
															className="faq-q" 
															dangerouslySetInnerHTML={{ __html: highlightHtml(qa.question, searchHighlight || "") }}
															onClick={() => scrollToQuestion(questionId)}
															style={{ cursor: 'pointer' }}
															title="Click to get direct link to this question"
														></span>
														<button
															className="faq-link-btn"
															onClick={(e) => {
																e.stopPropagation();
																copyQuestionLink(questionId);
															}}
															title="Copy direct link to this question"
															aria-label="Copy link"
														>
															{copiedId === questionId ? (
																<span className="faq-link-icon faq-link-copied">✓</span>
															) : (
																<span className="faq-link-icon">🔗</span>
															)}
														</button>
													</dt>
													<dd className="faq-a">
														<span dangerouslySetInnerHTML={{ __html: parts[0] || "" }} />
														{parts.slice(1).map((seg, i) => (
															<span className="faq-line" key={`seg-${i}`}>
																<span className="faq-arrow">›</span>
																<span dangerouslySetInnerHTML={{ __html: highlightHtml(seg, searchHighlight || "") }} />
															</span>
														))}
													</dd>
												</div>
											);
										})}
									</dl>
								</div>
							))}
						</section>
					))}
				</div>

				{/* Mobile floating scroll-to-top */}
				{showScrollTop && (
					<button
						className="faq-scroll-top"
						onClick={scrollToPageTop}
						aria-label="Back to top"
						title="Back to top"
					>
						<span className="faq-scroll-top-icon" aria-hidden>↑</span>
					</button>
				)}
			</div>
		</div>
		<div className="land-home noselect">
			<JoinCommunityAndFooter variant="faq" />
		</div>
		</>
	);
} 